import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { FiSearch, FiFilter, FiDownload, FiEye, FiEdit, FiTrash2, FiChevronRight, FiChevronDown, FiPlus, FiUser, FiX, FiUsers, FiBarChart2, FiTrendingUp, FiAward } from 'react-icons/fi';
import { AllPartnerStages, sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import { SortableTreeNode } from '../SortableTreeNode';

const AdminNetworkExplorer = () => {
  const [mlmData, setMlmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeNode, setActiveNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalReferrals: 0,
    averageDepth: 0
  });
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'list'
  const [filterOptions, setFilterOptions] = useState({
    rank: 'all',
    depth: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    rank: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Initialize sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch MLM data
  useEffect(() => {
    fetchMlmData();
  }, []);

  // Calculate stats when mlmData changes
  useEffect(() => {
    if (mlmData) {
      calculateStats();
    }
  }, [mlmData]);

  const calculateStats = () => {
    let totalMembers = 0;
    let activeMembers = 0;
    let totalReferrals = 0;
    let totalDepth = 0;
    let depthCount = 0;

    const traverseTree = (node) => {
      totalMembers++;
      if (node.rank === 'active') activeMembers++;

      if (node.mlmTree) {
        totalReferrals += node.mlmTree.children ? node.mlmTree.children.length : 0;
        if (node.mlmTree.depth) {
          totalDepth += node.mlmTree.depth;
          depthCount++;
        }
      }

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => traverseTree(child));
      }
    };

    mlmData.forEach(node => traverseTree(node));

    setStats({
      totalMembers,
      activeMembers,
      totalReferrals,
      averageDepth: depthCount > 0 ? (totalDepth / depthCount).toFixed(1) : 0
    });
  };

  const fetchMlmData = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest('api/admin/partners', {}, 'GET');

      if (response.data.success) {
        // Transform the data into a tree structure
        const treeData = transformToTreeStructure(response.data.data);
        setMlmData(treeData);
        setError(null);
      } else {
        setError('Failed to fetch MLM data');
        toast.error('Failed to fetch MLM data');
      }
    } catch (err) {
      setError('Failed to fetch MLM data. Please try again later.');
      console.error('Error fetching MLM data:', err);
      toast.error('Error fetching MLM data');
    } finally {
      setLoading(false);
    }
  };

  // Transform flat data into a tree structure
  const transformToTreeStructure = (data) => {
    // Find the root nodes (nodes with no parent)
    const rootNodes = data.filter(node => !node.mlmTree || !node.mlmTree.parent);

    if (rootNodes.length === 0) {
      return null;
    }

    // Create a map for quick lookup
    const nodeMap = new Map();
    data.forEach(node => {
      nodeMap.set(node._id, {
        ...node,
        children: []
      });
    });

    // Build the tree
    data.forEach(node => {
      if (node.mlmTree && node.mlmTree.parent) {
        const parentId = node.mlmTree.parent._id;
        const parentNode = nodeMap.get(parentId);
        if (parentNode) {
          parentNode.children.push(nodeMap.get(node._id));
        }
      }
    });

    // Return the root nodes
    return rootNodes.map(node => nodeMap.get(node._id));
  };

  // Toggle node expansion
  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  // Toggle all nodes
  const toggleAll = () => {
    if (allExpanded) {
      setExpandedNodes({});
    } else {
      const expandAllNodes = (node, nodes) => {
        if (node.children && node.children.length > 0) {
          nodes[node._id] = true;
          node.children.forEach(child => expandAllNodes(child, nodes));
        }
      };

      const allNodesExpanded = {};
      if (mlmData) {
        mlmData.forEach(rootNode => {
          expandAllNodes(rootNode, allNodesExpanded);
        });
      }
      setExpandedNodes(allNodesExpanded);
    }
    setAllExpanded(!allExpanded);
  };

  // Filter nodes based on search term and filter options
  const filterNodes = (node, term) => {
    if (!term && filterOptions.rank === 'all' && filterOptions.depth === 'all') return true;

    // Check search term
    const matchesSearch = !term ||
      node.fullName.toLowerCase().includes(term.toLowerCase()) ||
      node.sponsorCode.toLowerCase().includes(term.toLowerCase()) ||
      (node.email && node.email.toLowerCase().includes(term.toLowerCase()));

    // Check rank filter
    const matchesRank = filterOptions.rank === 'all' || node.rank === filterOptions.rank;

    // Check depth filter
    const matchesDepth = filterOptions.depth === 'all' ||
      (node.mlmTree && node.mlmTree.depth === parseInt(filterOptions.depth));

    if (!matchesSearch || !matchesRank || !matchesDepth) return false;

    if (node.children && node.children.length > 0) {
      return node.children.some(child => filterNodes(child, term));
    }

    return true;
  };

  // Get filtered data
  const filteredData = mlmData ? mlmData.filter(node => filterNodes(node, searchTerm)) : null;

  // Find a node by ID in the tree
  const findNode = (id, nodes) => {
    if (!nodes) return null;

    for (const node of nodes) {
      if (node._id === id) return node;
      if (node.children && node.children.length > 0) {
        const found = findNode(id, node.children);
        if (found) return found;
      }
    }
    return null;
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    if (mlmData) {
      const node = findNode(active.id, mlmData);
      setActiveNode(node);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      try {
        const response = await sendApiRequest('api/admin/mlm-tree/reorder', {
          nodeId: active.id,
          newParentId: over.id
        }, 'POST');

        if (response.data.success) {
          // Refresh the tree data
          fetchMlmData();
          toast.success('MLM structure updated successfully');
        } else {
          toast.error('Failed to update MLM structure');
        }
      } catch (err) {
        setError('Failed to reorder nodes. Please try again.');
        console.error('Error reordering nodes:', err);
        toast.error('Error reordering nodes');
      }
    }

    setActiveId(null);
    setActiveNode(null);
  };

  // Handle view node details
  const handleViewNode = (nodeId) => {
    const node = findNode(nodeId, mlmData);
    if (node) {
      setSelectedNode(node);
      setEditForm({
        rank: node.rank || ''
      });
      setIsEditing(false);
      setIsModalOpen(true);
    }
  };

  // Handle add child node
  const handleAddChild = async (parentId) => {
    try {
      const response = await sendApiRequest(`api/admin/mlm-tree/${parentId}/add-child`, {
        name: 'New Member',
        userId: 'NEW' + Date.now()
      }, 'POST');

      if (response.data.success) {
        // Refresh the tree data instead of manually updating
        fetchMlmData();

        // Expand the parent node
        setExpandedNodes(prev => ({
          ...prev,
          [parentId]: true
        }));

        toast.success('New member added successfully');
      } else {
        toast.error('Failed to add new member');
      }
    } catch (error) {
      console.error('Error adding new member:', error);
      toast.error('Error adding new member');
    }
  };

  // Handle delete node
  const handleDeleteNode = async (nodeId) => {
    if (window.confirm('Are you sure you want to delete this member? This will also delete all children.')) {
      try {
        const response = await sendApiRequest(`api/admin/mlm-tree/${nodeId}`, {}, 'DELETE');

        if (response.data.success) {
          // Refresh the tree data instead of manually updating
          fetchMlmData();

          toast.success('Member deleted successfully');
        } else {
          toast.error('Failed to delete member');
        }
      } catch (error) {
        console.error('Error deleting member:', error);
        toast.error('Error deleting member');
      }
    }
  };

  // Handle edit node
  const handleEditNode = (nodeId) => {
    const node = findNode(nodeId, mlmData);
    if (node) {
      setSelectedNode(node);
      setEditForm({
        rank: node.rank || ''
      });
      setIsEditing(true);
      setIsModalOpen(true);
    }
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!selectedNode) return;

    try {
      setIsSaving(true);
      const response = await sendApiRequest(`api/auth/update-profile`, {
        userId: selectedNode._id,
        rank: editForm.rank
      }, 'PUT');

      if (response.data.success) {
        // Update the node in the local state
        const updateNodeInTree = (nodes) => {
          return nodes.map(node => {
            if (node._id === selectedNode._id) {
              return { ...node, rank: editForm.rank };
            }
            if (node.children && node.children.length > 0) {
              return { ...node, children: updateNodeInTree(node.children) };
            }
            return node;
          });
        };

        setMlmData(prevData => updateNodeInTree(prevData));
        setIsEditing(false);
        toast.success('Member rank updated successfully');
      } else {
        toast.error('Failed to update member rank');
      }
    } catch (error) {
      console.error('Error updating member rank:', error);
      toast.error('Error updating member rank');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle export data
  const handleExport = () => {
    if (!mlmData) return;

    const exportData = JSON.stringify(mlmData, null, 2);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'mlm-network-data.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Data exported successfully');
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterOptions(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'manager':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'agent':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'partner':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">Loading MLM structure...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg shadow-md max-w-md">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
            </svg>
            <h3 className="text-lg font-bold">Error!</h3>
          </div>
          <p className="mt-2">{error}</p>
          <button
            onClick={fetchMlmData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header with Stats */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-3xl font-bold">
            MLM Network Explorer
          </h1>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button
              onClick={toggleAll}
              className="flex items-center px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors shadow-sm hover:shadow-md active:shadow-inner"
            >
              {allExpanded ? (
                <>
                  <FiChevronDown className="mr-2" />
                  Collapse All
                </>
              ) : (
                <>
                  <FiChevronRight className="mr-2" />
                  Expand All
                </>
              )}
            </button>
            <button
              onClick={handleExport}
              className="flex items-center px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors shadow-sm hover:shadow-md active:shadow-inner"
            >
              <FiDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm"
          >
            <div className="flex items-center">
              <div className="p-3 bg-blue-500 bg-opacity-30 rounded-full">
                <FiUsers className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-white text-opacity-80">Total Members</p>
                <p className="text-2xl font-bold">{stats.totalMembers}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm"
          >
            <div className="flex items-center">
              <div className="p-3 bg-green-500 bg-opacity-30 rounded-full">
                <FiTrendingUp className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-white text-opacity-80">Active Members</p>
                <p className="text-2xl font-bold">{stats.activeMembers}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm"
          >
            <div className="flex items-center">
              <div className="p-3 bg-purple-500 bg-opacity-30 rounded-full">
                <FiBarChart2 className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-white text-opacity-80">Total Referrals</p>
                <p className="text-2xl font-bold">{stats.totalReferrals}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm"
          >
            <div className="flex items-center">
              <div className="p-3 bg-amber-500 bg-opacity-30 rounded-full">
                <FiAward className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-white text-opacity-80">Average Depth</p>
                <p className="text-2xl font-bold">{stats.averageDepth}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <FiSearch className="absolute left-3 top-3.5 text-gray-400" />
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors shadow-sm hover:shadow-md active:shadow-inner ${showFilters
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
            >
              <FiFilter className="mr-2" />
              Filters
            </button>

            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('tree')}
                className={`px-4 py-2 rounded-lg transition-colors shadow-sm hover:shadow-md active:shadow-inner ${viewMode === 'tree'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
              >
                Tree View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg transition-colors shadow-sm hover:shadow-md active:shadow-inner ${viewMode === 'list'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
              >
                List View
              </button>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rank
                  </label>
                  <select
                    name="rank"
                    value={filterOptions.rank}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Ranks</option>
                    <option value="Associate">Associate</option>
                    <option value="Senior">Senior</option>
                    <option value="Executive">Executive</option>
                    <option value="Director">Director</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Depth
                  </label>
                  <select
                    name="depth"
                    value={filterOptions.depth}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Depths</option>
                    <option value="0">Level 0 (Root)</option>
                    <option value="1">Level 1</option>
                    <option value="2">Level 2</option>
                    <option value="3">Level 3</option>
                    <option value="4">Level 4</option>
                    <option value="5">Level 5+</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* MLM Tree */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-100 dark:border-gray-700">
        {mlmData && mlmData.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={filteredData ? filteredData.map(item => item._id) : []}
              strategy={verticalListSortingStrategy}
            >
              <div className="p-4 space-y-2">
                {filteredData && filteredData.map((rootNode) => (
                  <SortableTreeNode
                    key={rootNode._id}
                    node={rootNode}
                    expandedNodes={expandedNodes}
                    toggleNode={toggleNode}
                    onView={handleViewNode}
                    onAdd={handleAddChild}
                    onDelete={handleDeleteNode}
                    onEdit={handleEditNode}
                    isRoot={true}
                  />
                ))}
              </div>
            </SortableContext>

            <DragOverlay>
              {activeId && activeNode ? (
                <div className="bg-white dark:bg-gray-700 shadow-lg rounded-lg p-3 border border-blue-500">
                  <div className="flex items-center space-x-2">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white">
                      <FiUser />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{activeNode.fullName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{activeNode.sponsorCode}</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <div className="text-center py-12">
            <div className="inline-block p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
              <FiUsers className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-lg">No MLM data available</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">Add members to start building your network</p>
          </div>
        )}
      </div>

      {/* Node Details Modal */}
      <AnimatePresence>
        {isModalOpen && selectedNode && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 bg-black bg-opacity-50"
            />

            {/* Modal */}
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {isEditing ? 'Edit Member' : 'Member Details'}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.fullName}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Sponsor Code</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.sponsorCode}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.email || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.phone || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Join Date</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {new Date(selectedNode.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Rank</p>
                        {isEditing ? (
                          <select
                            name="rank"
                            value={editForm.rank}
                            onChange={handleEditFormChange}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select Rank</option>
                            {AllPartnerStages.map((option, index) => (
                              <option key={index} value={option.name}>
                                {option.name} ({option.value})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedNode.rank === 'active'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                              }`}
                          >
                            {selectedNode.rank || 'Not specified'}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Role</p>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(selectedNode.role)}`}
                        >
                          {selectedNode.role || 'Not specified'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Network Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Network Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Direct Referrals</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.children ? selectedNode.children.length : 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Downline</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.mlmTree?.ancestors?.length || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Level</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.mlmTree?.depth || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Parent</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.mlmTree?.parent ? selectedNode.mlmTree.parent.fullName : 'None'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Performance */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Performance
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Sales</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          ₹{selectedNode.sales?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Commission</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          ₹{selectedNode.commission?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Achievements</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {selectedNode.achievements?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors shadow-sm hover:shadow-md active:shadow-inner mr-2"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm hover:shadow-md active:shadow-inner flex items-center"
                      >
                        {isSaving ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEditNode(selectedNode._id)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm hover:shadow-md active:shadow-inner mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors shadow-sm hover:shadow-md active:shadow-inner"
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminNetworkExplorer;
